import { Err, Ok, Result } from '@gametailors/web-core/classes';
import { LpSchema } from '../../../typings/schema';
import { EndpointError } from '../../../services/errors';
import { GenerateErrorCode } from '../../../services/recommendation-api.service';
import { GeneratedContentDeserializer } from './generated-content-deserializer';
import { GeneratorStep, MechanicGenerators } from './mechanic-generators';

type LpInstance = LpSchema['all'];

/**
 * Who the content is for. With "instruction" a therapist is present, so every topic is open; with "homework" only the
 * topics the client already practised are.
 */
export type GeneratorContext = 'instruction' | 'homework';

/** Runs a generator for the client the content is meant for. */
export type RunGenerator = (
  generatorId: string,
  inputs: Record<string, unknown>,
) => Promise<Result<unknown, EndpointError<GenerateErrorCode>>>;

export class MechanicContentError extends Error {
  constructor(
    readonly generatorId: string,
    /** Why it failed, as the generator or the check of its result put it. */
    readonly reason: string,
    readonly endpointError?: EndpointError<GenerateErrorCode>,
  ) {
    super(`${generatorId}: ${reason}`);
    this.name = 'MechanicContentError';
  }
}

/** Generates the config and the content of a mechanic with its generators (see {@link MechanicGenerators}). */
export class MechanicContentGenerator {
  constructor(
    private readonly run: RunGenerator,
    private readonly deserializer: GeneratedContentDeserializer,
    private readonly context: GeneratorContext,
  ) {}

  public generateConfig<TConfig extends LpInstance, TAssignment extends LpInstance>(
    generators: MechanicGenerators<TConfig, TAssignment>,
    inputs: Record<string, unknown> = {},
  ): Promise<Result<TConfig, MechanicContentError>> {
    return this.generate(generators.config, inputs, undefined);
  }

  public generateAssignment<TConfig extends LpInstance, TAssignment extends LpInstance>(
    generators: MechanicGenerators<TConfig, TAssignment>,
    config: TConfig,
    inputs: Record<string, unknown> = {},
  ): Promise<Result<TAssignment, MechanicContentError>> {
    return this.generate(generators.assignment, { ...inputs, config }, config);
  }

  private async generate<T extends LpInstance, TFrom>(
    step: GeneratorStep<T, TFrom>,
    inputs: Record<string, unknown>,
    from: TFrom,
  ): Promise<Result<T, MechanicContentError>> {
    const output = await this.run(step.generatorId, { ...inputs, context: this.context });
    if (output.isErr()) {
      const error = output.unwrapErr();
      return Err(new MechanicContentError(step.generatorId, this.reasonOf(error), error));
    }

    const value = await this.deserializer.deserialize<T>(output.unwrap(), step.dataType);
    if (value.isErr()) {
      return Err(new MechanicContentError(step.generatorId, value.unwrapErr()));
    }

    const completed = step.complete ? step.complete(value.unwrap(), from) : Ok(value.unwrap());
    return completed.isErr() ? Err(new MechanicContentError(step.generatorId, completed.unwrapErr())) : Ok(completed.unwrap());
  }

  private reasonOf(error: EndpointError<GenerateErrorCode>): string {
    switch (error.code) {
      case 'GENERATOR_FAILED':
        return error.message;
      default:
        return `the request failed with status ${error.status}`;
    }
  }
}
